const VIDEO_CON_SELECTOR = '.video_con';
const VIDEO_DUMMY_SELECTOR = '.video_dummy';

const removeScrollLockClasses = () => {
  const videoCon = document.querySelector(VIDEO_CON_SELECTOR);
  const videoDummy = document.querySelector(VIDEO_DUMMY_SELECTOR);
  const headers = document.querySelectorAll('.header, #header');

  videoCon?.classList.remove('fixed', 'm_fixed', 'attedanceFixed');
  videoDummy?.classList.remove('on');
  headers.forEach(header => header.classList.remove('videofixed'));

  return videoCon;
};

// 브라우저별 vendor-prefixed fullscreen API 통합.
// - 표준: document.fullscreenElement / Element.requestFullscreen / document.exitFullscreen
// - WebKit (iOS Safari 등): webkit 접두사
// - Firefox / IE 레거시도 같이 fallback
const getFullscreenElement = () =>
  document.fullscreenElement
  || document.webkitFullscreenElement
  || document.mozFullScreenElement
  || document.msFullscreenElement
  || null;

const requestFullscreenOn = (el) => {
  if (!el) return Promise.reject(new Error('no element'));
  const rfs = el.requestFullscreen
    || el.webkitRequestFullscreen
    || el.mozRequestFullScreen
    || el.msRequestFullscreen;
  if (!rfs) return Promise.reject(new Error('Fullscreen API not supported'));
  try {
    const result = rfs.call(el);
    return result && typeof result.then === 'function' ? result : Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

const exitFullscreenIfActive = () => {
  if (!getFullscreenElement()) return Promise.resolve();
  const efs = document.exitFullscreen
    || document.webkitExitFullscreen
    || document.mozCancelFullScreen
    || document.msExitFullscreen;
  if (!efs) return Promise.resolve();
  try {
    const result = efs.call(document);
    return result && typeof result.then === 'function' ? result : Promise.resolve();
  } catch (e) {
    return Promise.resolve();
  }
};

// iOS Safari는 document-level requestFullscreen이 없어서 native API로 URL바를 못 숨긴다.
// 대신 iOS가 스크롤을 감지하면 자동으로 URL바를 접는 특성을 이용해,
// fullscreen 진입 직전에 programmatic 스크롤로 URL바를 숨긴다.
// 관리자 뷰는 Control 사이드바 때문에 자연스럽게 스크롤되어 URL바가 숨은 상태에서 fullscreen에 진입하지만,
// 참가자 뷰는 컨텐츠가 짧아 scrollY=0 상태로 진입해 URL바가 남는 문제가 있었음.
const IOS_SCROLL_AMOUNT = 200;
let iosOriginalBodyMinHeight = null;

const applyIOSUrlBarHideTrick = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 2 && /Mac/.test(navigator.platform));
  if (!isIOS) return false;

  // body가 viewport보다 짧아 스크롤 여지가 없으면 scrollTo가 무시됨 → minHeight 임시 주입.
  // 충분히 큰 minHeight(viewport*2 + 여유)를 줘서 진입 후에도 사용자가 swipe로 page-down 할
  // 여지가 남도록 한다. iOS Safari는 사용자 swipe로 scrollTop이 늘어날 때 url 바를 자동 숨김.
  iosOriginalBodyMinHeight = document.body.style.minHeight;
  document.body.style.minHeight = `${window.innerHeight * 2 + IOS_SCROLL_AMOUNT}px`;
  window.scrollTo(0, IOS_SCROLL_AMOUNT);
  return true;
};

const restoreIOSBodyMinHeight = () => {
  if (iosOriginalBodyMinHeight !== null) {
    document.body.style.minHeight = iosOriginalBodyMinHeight;
    iosOriginalBodyMinHeight = null;
  }
};

// Samsung Internet 감지.
// Samsung Internet은 native fullscreen API 호출 시 자체 "Video Assistant" 컨트롤 오버레이를 띄우는데
// 이건 페이지에서 비활성화할 수 없어 유저 경험을 해친다.
// 이 브라우저에 한해 native fullscreen API 호출을 스킵하고 CSS pseudo fullscreen만 사용.
const isSamsungBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  return /SamsungBrowser/i.test(navigator.userAgent);
};

export const toggleCssFullscreen = (enter, setCssFullscreen) => {
  const shouldEnter = Boolean(enter);
  const videoCon = removeScrollLockClasses();

  if (shouldEnter) {
    const isIOS = applyIOSUrlBarHideTrick();

    // video_con은 즉시 fullscreen 스타일 적용 (뷰포트 덮기)
    videoCon?.classList.add('css-fullscreen');

    // body.css-fullscreen-active는 "즉시" 적용해야 한다.
    // 이 클래스에 survey 모달/팝업 모달의 z-index boost가 포함되어 있어 지연시키면
    // 모달이 video_con(z-index:9998) 뒤로 가려진다.
    document.body.classList.add('css-fullscreen-active');
    // html(documentElement)에도 함께 부여해 root 스크롤바를 차단한다.
    // body에만 overflow: hidden을 줘도 브라우저 root는 html이라 페이지 스크롤이 남는 회귀가 있어 같이 처리.
    document.documentElement.classList.add('css-fullscreen-active');

    // iOS의 경우 CSS의 `overflow: hidden`이 즉시 걸리면 URL바 숨김 트릭(scrollTo)이
    // 무효화되므로 overflow만 인라인으로 visible 오버라이드 후 다음 프레임에 해제.
    if (isIOS) {
      document.body.style.overflow = 'visible';
      requestAnimationFrame(() => {
        document.body.style.overflow = '';
      });
    }

    // 1순위: documentElement에 fullscreen (PC에서 모달/오버레이가 fullscreen 안에서 같이 보이도록)
    // 2순위: videoCon에 fullscreen
    // iOS Safari는 두 API 모두 실패할 가능성이 큰데, 정책상 iOS에서는 native 비디오 fullscreen 대신
    // CSS 오버레이 + URL바 숨김 트릭만 사용 (커스텀 버튼/서베이 모달이 보여야 하므로).
    // 따라서 webkitEnterFullscreen 호출은 의도적으로 추가하지 않음.
    //
    // orientation lock은 사용하지 않는다. iPhone처럼 사용자의 현재 방향(세로/가로)을 유지.
    // (Android Chrome에서 lock 거부 시 회전이 시작됐다가 되돌아가는 깜빡임 발생 사례 때문에 제거)
    //
    // Samsung Internet은 native fullscreen 호출 시 자체 "Video Assistant" 컨트롤 오버레이를
    // 띄우는데 페이지에서 비활성화할 수 없어 사용자 UX가 망가진다.
    // 따라서 Samsung Internet에 한해 native fullscreen 호출을 스킵하고 CSS pseudo fullscreen만 사용.
    if (!isSamsungBrowser()) {
      requestFullscreenOn(document.documentElement)
        .catch(() => requestFullscreenOn(videoCon))
        .catch(() => {});
    }

    window.history.pushState({ cssFullscreen: true }, '', window.location.href);
  } else {
    videoCon?.classList.remove('css-fullscreen');
    document.body.classList.remove('css-fullscreen-active');
    document.documentElement.classList.remove('css-fullscreen-active');
    document.body.style.overflow = '';
    restoreIOSBodyMinHeight();
    exitFullscreenIfActive();
  }

  if (typeof setCssFullscreen === 'function') {
    setCssFullscreen(shouldEnter);
  }
};

export const setupCssFullscreenListeners = (getCssFullscreen, setCssFullscreen) => {
  const isCssFullscreen = () => {
    if (typeof getCssFullscreen !== 'function') {
      return false;
    }

    return Boolean(getCssFullscreen());
  };

  const handleKeyDown = event => {
    if (event.key === 'Escape' && isCssFullscreen()) {
      // 네이티브 fullscreen 중에는 일부 브라우저가 ESC를 페이지에 dispatch 하지 않고 자체 소비함.
      // 그 케이스는 fullscreenchange 핸들러가 보완 처리. keydown이 도달하는 케이스는 여기서 즉시 종료.
      event.preventDefault();
      event.stopImmediatePropagation();
      toggleCssFullscreen(false, setCssFullscreen);
    }
  };

  // fullscreenchange로 네이티브 종료를 감지하되, 모달 DOM 존재 시는 모달-트리거 자동 종료로 간주하여 CSS 유지.
  // (swal 등 모달이 fullscreen 중 마운트되면 Chrome이 자동으로 네이티브 fullscreen 종료시키는 회귀 보호.)
  const hasOpenModalInDom = () => Boolean(
    document.querySelector('.swal-overlay--show-modal') ||
    document.querySelector('.swal-overlay.swal-overlay--show-modal') ||
    document.querySelector('.swal2-container') ||
    document.querySelector('.modal.show')
  );

  const handlePopState = () => {
    if (isCssFullscreen()) {
      toggleCssFullscreen(false, setCssFullscreen);
    }
  };

  // orientation 변화에 따른 자동 exit 로직은 제거.
  // (이전: 모바일에서 landscape→portrait 회전 시 CSS fullscreen 자동 종료 → portrait fullscreen이 정상 케이스가 된 후로 의미 없어짐)

  // fullscreenchange 자동 연동 — 네이티브 종료를 감지하여 CSS도 동기화.
  // 일부 브라우저는 네이티브 fullscreen 중 ESC 누르면 keydown을 페이지에 전달하지 않고 자체 소비함 →
  // keydown 핸들러로는 첫 ESC를 못 잡으므로 fullscreenchange가 필수 보완 신호.
  // 단, swal 등 모달 DOM 추가로 인한 네이티브 자동 종료 케이스는 CSS 유지 (회귀 보호).
  //   - 모달이 fullscreen 중 마운트되면 Chrome이 자체 정책으로 네이티브 fullscreen 종료
  //   - 그 때 CSS까지 풀리면 swal/survey modal이 일반 페이지에 노출되어 사용성 깨짐
  //   - 모달 DOM이 있으면 모달-트리거로 판단해 CSS 유지
  const handleFullscreenChange = () => {
    if (getFullscreenElement()) return;            // 진입은 무시
    if (!isCssFullscreen()) return;                // CSS 상태 이미 정리됨
    if (hasOpenModalInDom()) return;               // 모달-트리거 자동 종료 → CSS 유지
    toggleCssFullscreen(false, setCssFullscreen);
  };

  // iOS `<video>`의 webkitEnterFullscreen 해제는 사용자가 iOS 네이티브 UI로 명시적으로 종료한 것이므로
  // 이 경우만 CSS fullscreen을 함께 해제한다.
  const handleVideoWebkitEnd = () => {
    if (isCssFullscreen()) {
      toggleCssFullscreen(false, setCssFullscreen);
    }
  };

  // capture 단계로 등록 — JW Player / 다른 element-level 핸들러의 stopPropagation 영향 없이 항상 먼저 실행.
  // window + document 양쪽에 등록하여 어느 한쪽에서 이벤트가 가로채지는 케이스도 방어.
  window.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('keydown', handleKeyDown, true);
  window.addEventListener('popstate', handlePopState);
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  document.addEventListener('MSFullscreenChange', handleFullscreenChange);
  document.addEventListener('webkitendfullscreen', handleVideoWebkitEnd, true);

  return () => {
    window.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('keydown', handleKeyDown, true);
    window.removeEventListener('popstate', handlePopState);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    document.removeEventListener('webkitendfullscreen', handleVideoWebkitEnd, true);
  };
};
